import React from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';
import { Box, IconButton, Typography } from '@material-ui/core';

import { BlurWithLogin, CustomIcon, Module, Translation } from '../../common';

import { mockData } from './mockData';
import TimelineSlider from './TimelineSlider';
import greenLight from '../../../assets/img/status/01_ampelwide-gruen.png';
import redLight from '../../../assets/img/status/03_ampelwide-rot.png';

const TIMELINE_CENSORED = 'TIMELINE_CENSORED';
const TIMELINE_HEADER = isClientPortal =>
  `BOX_TIMELINE_HEADER${isClientPortal ? '_CLIENT' : ''}`;

const SlickArrowLeft = ({ currentSlide, slideCount, ...props }) => (
  <IconButton
    {...props}
    aria-label="previous"
    className={
      'slick-prev slick-arrow' + (currentSlide === 0 ? ' slick-disabled' : '')
    }
    aria-hidden="true"
    aria-disabled={currentSlide === 0}
  >
    <CustomIcon icon="chevronLeft" size="small" variant="primaryContrast" />
  </IconButton>
);

const SlickArrowRight = ({ currentSlide, slideCount, ...props }) => (
  <IconButton
    aria-label="next"
    {...props}
    className={
      'slick-next slick-arrow' +
      (currentSlide === slideCount - 1 ? ' slick-disabled' : '')
    }
    aria-hidden="true"
    aria-disabled={currentSlide === slideCount - 1}
  >
    <CustomIcon icon="chevronLeft" size="small" variant="primaryContrast" />
  </IconButton>
);

const sliderSettings = (isMobileSize, sliderClass, initialSlide) => ({
  dots: false,
  infinite: false,
  initialSlide: initialSlide,
  slidesToShow: isMobileSize ? 1 : 3,
  slidesToScroll: 1,
  vertical: false,
  verticalSwiping: false,
  useTransform: false,
  swipeToSlide: true,
  className: sliderClass,
  prevArrow: <SlickArrowLeft />,
  nextArrow: <SlickArrowRight />
});

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    background: `radial-gradient(100% 138.46% at 0% 0%, ${theme.palette.primary.main} 27.6%, ${theme.palette.primary.light} 100%)`,
    padding: '40px 16px 16px 16px'
  },
  rootSmall: {
    paddingTop: 16
  },
  titleRow: {
    minHeight: 64,
    paddingLeft: 16,
    paddingRight: 85,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: theme.palette.primary.contrastText
  },
  status: {
    width: 77,
    height: 136,
    position: 'absolute',
    top: 0,
    right: 0
  },
  slider: {
    margin: '24px 16px 0 16px',

    '& .slick-track': {
      display: 'flex'
    },

    '& .slick-slide': {
      height: 'inherit',

      '& > div': {
        display: 'flex',
        height: '100%'
      }
    },

    '&:before': {
      position: 'absolute',
      content: '""',
      left: 0,
      right: 0,
      top: 5,
      height: 2,
      background: theme.palette.text.disabled
    },

    '& .slick-prev,& .slick-next': {
      position: 'absolute',
      zIndex: 1,
      top: 5,
      width: 32,
      height: 32,
      padding: 4,
      background: theme.palette.primary.main,

      '&:hover': {
        background: theme.palette.primary.light
      },

      '&:before': {
        content: '""'
      }
    },

    '& .slick-prev': {
      left: -40
    },

    '& .slick-next': {
      right: -40,
      '& svg': {
        transform: 'rotate(180deg)'
      }
    },

    '& .slick-disabled': {
      background: theme.palette.action.disabled,
      cursor: 'default',
      pointerEvents: 'none',

      '& path': {
        fill: theme.palette.text.hint
      }
    }
  },
  sliderCompact: {
    '& .slick-prev': {
      left: -21
    },

    '& .slick-next': {
      right: -21
    }
  },
  sliderSmall: {
    margin: '24px 28px 0 28px',

    '& .slick-prev': {
      left: -36
    },

    '& .slick-next': {
      right: -36
    }
  }
}));

const Timeline = ({
  className,
  id,
  isTabletSize,
  isMobileSize,
  items,
  blur,
  isClientPortal,
  onBlurLoginClick,
  isPaidFully
}) => {
  const classes = useStyles();
  const activeIndex = (items || []).findIndex(({ current }) => current);

  const settings = sliderSettings(
    isMobileSize,
    classNames(
      classes.slider,
      isTabletSize && classes.sliderCompact,
      isMobileSize && classes.sliderSmall
    ),
    activeIndex !== -1
      ? isMobileSize
        ? activeIndex
        : activeIndex - 1
      : undefined
  );

  return (
    <Module
      id={id}
      className={classNames(
        classes.root,
        isMobileSize && classes.rootSmall,
        className
      )}
    >
      <Box className={classes.titleRow}>
        <Typography variant="h3">
          <Translation inline id="timeline-header">
            {TIMELINE_HEADER(isClientPortal)}
          </Translation>
        </Typography>
        <img
          id={`timeline-header-status-${isPaidFully ? 'green' : 'red'}`}
          className={classes.status}
          src={isPaidFully ? greenLight : redLight}
          alt="status"
        />
      </Box>
      <BlurWithLogin
        blur={blur}
        descriptionKey={TIMELINE_CENSORED}
        mockData={
          <TimelineSlider
            isTabletSize={isTabletSize}
            isMobileSize={isMobileSize}
            settings={settings}
            items={mockData}
            isClientPortal={isClientPortal}
            isPaidFully={isPaidFully}
          />
        }
        onButtonClick={onBlurLoginClick}
        buttonId="page.home.component.timeline.logout-button"
      >
        {items && (
          <TimelineSlider
            isTabletSize={isTabletSize}
            isMobileSize={isMobileSize}
            settings={settings}
            items={items}
            isClientPortal={isClientPortal}
            isPaidFully={isPaidFully}
          />
        )}
      </BlurWithLogin>
    </Module>
  );
};

export default Timeline;
